import { computed, Ref } from 'vue'
import { FragmentType, useFragment } from '@/gql'
import {
  FieldableFormCustomFieldFragment,
  FieldableFormCustomFieldValueFragment,
} from '@/components/Fieldable/graphql/fragments'
import {
  CustomFieldType,
  CustomFieldTypeValueInput,
  FieldableFormCustomFieldFragment as FieldableFormCustomFieldFragmentGQL,
} from '@/gql/graphql'
import { isCustomFieldRequired } from '@/components/Fields/utils/CustomFieldUtils'
import type { FieldableCustomFieldExistingFile } from '@/components/Fieldable/types'

interface MappedField {
  field: FragmentType<typeof FieldableFormCustomFieldFragment>
  fieldTyped: FieldableFormCustomFieldFragmentGQL
  fieldValue?: CustomFieldTypeValueInput
  existingFiles: FieldableCustomFieldExistingFile[]
}

export default function useFormCustomFieldValues(
  fields: Ref<FragmentType<typeof FieldableFormCustomFieldFragment>[]>,
  fieldValues: Ref<CustomFieldTypeValueInput[]>,
  fieldValueFragments?: Ref<FragmentType<typeof FieldableFormCustomFieldValueFragment>[]>,
) {
  const filesByFieldId = computed<Record<string, FieldableCustomFieldExistingFile[]>>(() => {
    const map: Record<string, FieldableCustomFieldExistingFile[]> = {}
    const fragments = fieldValueFragments?.value ?? []
    for (const fragment of fragments) {
      const valueTyped = useFragment(FieldableFormCustomFieldValueFragment, fragment)
      if (
        valueTyped.customField.fieldType === CustomFieldType.FileUpload &&
        valueTyped.file
      ) {
        const fieldId = valueTyped.customField.id
        if (!map[fieldId]) {
          map[fieldId] = []
        }
        map[fieldId].push({
          globalId: valueTyped.file.id,
          name: valueTyped.file.name,
          downloadUrl: valueTyped.file.url,
          humanReadableSize: valueTyped.file.humanReadableSize,
        })
      }
    }
    return map
  })

  const mappedFields = computed<MappedField[]>(() =>
    fields.value.map((field) => {
      const fieldTyped = useFragment(FieldableFormCustomFieldFragment, field)
      return {
        field,
        fieldTyped,
        fieldValue: fieldValues.value?.find((fieldValue) =>
          [
            fieldValue.singleSelect?.id,
            fieldValue.freeText?.id,
            fieldValue.multiSelect?.id,
            fieldValue.fileUpload?.id,
            fieldValue.date?.id,
            fieldValue.boolean?.id,
          ].includes(fieldTyped.id),
        ),
        existingFiles: filesByFieldId.value[fieldTyped.id] ?? [],
      }
    }),
  )

  const mappedAndSortedFields = computed(() =>
    mappedFields.value.sort((valueA, valueB) => {
      const aIsRequired = isCustomFieldRequired(valueA.fieldTyped.settings)
      const bIsRequired = isCustomFieldRequired(valueB.fieldTyped.settings)
      if (aIsRequired && !bIsRequired) {
        return -1
      }
      if (!aIsRequired && bIsRequired) {
        return 1
      }
      return 0
    }),
  )

  const parseUpdateFieldValues = (
    fieldId: string,
    value: CustomFieldTypeValueInput,
  ): CustomFieldTypeValueInput[] => {
    const index = mappedFields.value.findIndex(
      (localMappedField) => localMappedField.fieldTyped.id === fieldId,
    )

    const updatedFieldValues = mappedFields.value
    updatedFieldValues[index].fieldValue = value

    const managedFieldIds = new Set(mappedFields.value.map((mf) => mf.fieldTyped.id))

    const otherValues = fieldValues.value.filter((fv) => {
      const id =
        fv.singleSelect?.id ??
        fv.freeText?.id ??
        fv.multiSelect?.id ??
        fv.fileUpload?.id ??
        fv.date?.id ??
        fv.boolean?.id
      return id && !managedFieldIds.has(id)
    })

    const ownValues = updatedFieldValues
      .map((mappedField) => mappedField.fieldValue)
      .filter((fieldValue) => fieldValue) as CustomFieldTypeValueInput[]

    return [...otherValues, ...ownValues]
  }

  return {
    mappedFields,
    mappedAndSortedFields,
    parseUpdateFieldValues,
  }
}
